import { BrowserAuthError, EventType, PublicClientApplication, type AccountInfo, type AuthenticationResult } from '@azure/msal-browser';
import { config } from './config';
import { AuthFailedError, AuthRedirectError } from './errors';

/**
 * MSAL (PKCE) sign-in against the "Process Hub" app registration. D-015.
 * The access token for `apiScope` is sent as a bearer token to the Function App, where
 * App Service Authentication validates it. No secrets in the browser, by construction.
 */

export const loginRequest = { scopes: [config.apiScope] };

export const msalInstance = new PublicClientApplication({
  auth: {
    clientId: config.clientId,
    authority: `https://login.microsoftonline.com/${config.tenantId}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: { cacheLocation: 'sessionStorage' },
});

export async function initAuth(): Promise<void> {
  if (config.authDisabled) return;
  await msalInstance.initialize();
  const existing = msalInstance.getAllAccounts()[0];
  if (existing && !msalInstance.getActiveAccount()) msalInstance.setActiveAccount(existing);
  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      msalInstance.setActiveAccount((event.payload as AuthenticationResult).account);
    }
  });
  // A redirect response that cannot be processed must not stop the app from rendering: MSAL
  // clears its own temporary cache on the way out, so the next sign-in starts clean. D-055.
  try {
    await msalInstance.handleRedirectPromise();
  } catch (err) {
    console.warn('Ignoring an unusable sign-in redirect response.', err);
  }
}

function activeAccount(): AccountInfo | null {
  return msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
}

/**
 * Redirect-loop guard. Entra hands a SPA a refresh token with a fixed 24-hour lifetime, so a
 * long-open tab always reaches a point where only interaction will do. Two attempts inside a
 * minute is a real sign-in problem, not an expired token, and looping would hide it.
 */
const GUARD_KEY = 'ph.auth.redirects';
const GUARD_WINDOW_MS = 60_000;
const GUARD_LIMIT = 2;

function countRedirect(): number {
  let first = Date.now();
  let n = 0;
  try {
    const raw = sessionStorage.getItem(GUARD_KEY);
    const prior = raw ? (JSON.parse(raw) as { n: number; first: number }) : null;
    if (prior && Date.now() - prior.first <= GUARD_WINDOW_MS) {
      first = prior.first;
      n = prior.n;
    }
    sessionStorage.setItem(GUARD_KEY, JSON.stringify({ n: n + 1, first }));
  } catch {
    // Storage denied (private mode); the count stays at one and interaction still proceeds.
  }
  return n + 1;
}

function clearGuard(): void {
  try {
    sessionStorage.removeItem(GUARD_KEY);
  } catch {
    // Nothing to clear.
  }
}

/** One redirect per page, however many calls fail at once. */
let redirecting: Promise<never> | null = null;

/**
 * Drops the unusable tokens and sends the person back through Entra. Resolving is not an
 * option: returning a null token here is what used to let `api.ts` fire the call anonymously,
 * which Easy Auth answered with a CORS-blocked 401 that surfaced as a transport failure.
 */
function signInAgain(account: AccountInfo | null, cause: string): Promise<never> {
  redirecting ??= (async () => {
    if (countRedirect() > GUARD_LIMIT) {
      clearGuard();
      throw new AuthFailedError(`Sign-in did not complete. ${cause}`);
    }
    // Clearing first means a plain refresh recovers even if this navigation never happens:
    // with no account cached, the sign-in gate starts a fresh login.
    if (account) await msalInstance.clearCache({ account });
    try {
      await msalInstance.loginRedirect(account ? { ...loginRequest, loginHint: account.username } : loginRequest);
    } catch (err) {
      // The sign-in gate started its own redirect the moment the account went away; that one
      // is already taking the person to Entra.
      if (!(err instanceof BrowserAuthError && err.errorCode === 'interaction_in_progress')) throw err;
    }
    throw new AuthRedirectError();
  })();
  return redirecting;
}

/** Access token for the API, or null when sign-in is disabled for local development. */
export async function getAccessToken(): Promise<string | null> {
  if (config.authDisabled) return null;
  const account = activeAccount();
  if (!account) return signInAgain(null, 'No account is signed in.');
  try {
    const r = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
    clearGuard();
    return r.accessToken;
  } catch (err) {
    /*
     * Every silent failure is treated the same way. Catching only InteractionRequiredAuthError
     * left the common case unhandled: an expired SPA refresh token comes back as `invalid_grant`
     * (AADSTS700084, or AADSTS700082 after inactivity), which MSAL raises as ServerError. That
     * rethrow dead-ended in the UI as "The API could not be reached" on every reload, because
     * the dead token sat in sessionStorage, which a refresh preserves. D-055.
     */
    return signInAgain(account, err instanceof Error ? err.message : String(err));
  }
}

export function signOut(): void {
  if (config.authDisabled) return;
  clearGuard();
  void msalInstance.logoutRedirect({ account: activeAccount() ?? undefined });
}
